import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { CATALOG_CREATION_ACCESS_ADD_DEFAULT_VALUES } from '../catalogCreationAccess.consts';
import {
  CatalogCreationAccessCatalogDates,
  CatalogCreationAccessEntitiesToAdd,
  CatalogCreationAccessEntityOption,
  CatalogCreationAccessFieldName,
} from '../catalogCreationAccess.types';
import {
  buildCatalogCreationAccessEntityNames,
  resolveCatalogAccessDateHelperText,
  resolveCatalogCreationAccessAddConfig,
} from '../catalogCreationAccess.utils';
import {
  CatalogCreationAccessAddFormValues,
  buildCatalogCreationAccessAddSchema,
} from '../catalogCreationAccessAdd.schema';

interface UseCatalogCreationAccessAddModalParams {
  field: CatalogCreationAccessFieldName;
  catalogDates: CatalogCreationAccessCatalogDates;
  catalogId?: string;
  onConfirm: (entitiesToAdd: CatalogCreationAccessEntitiesToAdd) => void;
}

export const useCatalogCreationAccessAddModal = ({
  field,
  catalogDates,
  catalogId,
  onConfirm,
}: UseCatalogCreationAccessAddModalParams) => {
  const { translate } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<CatalogCreationAccessEntityOption[]>([]);

  const config = resolveCatalogCreationAccessAddConfig({ field, catalogId });

  const { control, handleSubmit } = useForm<CatalogCreationAccessAddFormValues>({
    resolver: zodResolver(buildCatalogCreationAccessAddSchema(translate)),
    defaultValues: CATALOG_CREATION_ACCESS_ADD_DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const entityIds = useWatch({ control, name: 'entityIds' });

  const handleChangeSelectedOptions = (options: CatalogCreationAccessEntityOption[]) => {
    setSelectedOptions(options);
  };

  const handleConfirm = handleSubmit((formValues) => {
    onConfirm({
      entityIds: formValues.entityIds,
      entityNames: buildCatalogCreationAccessEntityNames({
        options: selectedOptions,
        labelField: config.labelField,
      }),
      startDate: formValues.startDate,
      endDate: formValues.endDate,
    });
  });

  return {
    config,
    control,
    selectedOptions,
    isConfirmDisabled: entityIds.length === 0,
    startDateHelperText: resolveCatalogAccessDateHelperText({
      translate,
      fieldLabel: LangEnum.GLOBAL_INITIAL_DATE,
      catalogDate: catalogDates.startDate,
    }),
    endDateHelperText: resolveCatalogAccessDateHelperText({
      translate,
      fieldLabel: LangEnum.GLOBAL_FINAL_DATE,
      catalogDate: catalogDates.endDate,
    }),
    handleChangeSelectedOptions,
    handleConfirm,
  };
};
